import { useState, useEffect, useCallback } from 'react';
import { useDBA } from '@/contexts/DBAContext';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, ExternalLink, Copy, AlertTriangle, Link2Off, Building2, Globe2 } from 'lucide-react';
import { HighVisibilityPageHeader, HighVisibilityEmptyState } from '@/components/ui/high-visibility-mobile';

const industryLabels = {
  'junk-removal': 'Junk Removal',
  'pressure-washing': 'Pressure Washing',
  'property-cleanup': 'Property Cleanup',
  'deep-cleaning': 'Deep Cleaning',
  junk_removal: 'Junk Removal',
  cleaning: 'Cleaning',
  pressure_washing: 'Pressure Washing',
  lawn_care: 'Lawn Care',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
  painting: 'Painting',
  other: 'Other',
};

export default function DBAManagementPage() {
  const { dbas, fetchDBAs } = useDBA();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState(['']);
  const [slugDraft, setSlugDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const openAdd = () => {
    setEditing(null);
    setCategories(['']);
    setSlugDraft('');
    setShowForm(true);
  };

  const openEdit = (dba) => {
    setEditing(dba);
    setCategories(dba.service_categories?.length > 0 ? dba.service_categories : ['']);
    setSlugDraft(dba.slug || '');
    setShowForm(true);
  };

  const slugChanged = editing && slugDraft && slugDraft.trim().toLowerCase() !== editing.slug;

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    const f = new FormData(e.target);
    const validCats = categories.filter(c => c.trim());
    const payload = {
      name: f.get('name'),
      slug: f.get('slug'),
      industry: f.get('industry'),
      description: f.get('description'),
      color: f.get('color'),
      service_categories: validCats,
      contact_phone: f.get('contact_phone'),
      contact_email: f.get('contact_email'),
      contact_address: f.get('contact_address'),
      tagline: f.get('tagline') || '',
      business_hours: f.get('business_hours') || '',
      website_url: f.get('website_url') || '',
      review_link: f.get('review_link') || '',
      status: f.get('status') || 'active',
      parent_business_slug: f.get('parent_business_slug') || '',
      service_line: f.get('service_line') || f.get('industry'),
      service_area: f.get('service_area') || '',
    };
    try {
      if (editing) {
        await api.put(`/dbas/${editing.id}`, payload);
      } else {
        await api.post('/dbas', payload);
      }
      setShowForm(false);
      fetchDBAs();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const openDelete = (dba) => {
    setDeleteTarget(dba);
    setDeleteConfirm('');
    setDeleteError('');
  };

  const deleteDBA = async () => {
    if (!deleteTarget) return;
    setDeleteError('');
    try {
      await api.delete(`/dbas/${deleteTarget.id}`, { data: { confirm: deleteConfirm } });
      setDeleteTarget(null);
      setDeleteConfirm('');
      fetchDBAs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.references) {
        const refs = Object.entries(detail.references).map(([key, value]) => `${key.replaceAll('_', ' ')}: ${value}`).join(', ');
        setDeleteError(`${detail.message} Related records: ${refs}`);
      } else {
        setDeleteError(formatApiError(detail));
      }
    }
  };

  const removeAlias = async (dba, alias) => {
    if (!window.confirm(`Remove old URL alias /intake/${alias}? External landing pages still using it will stop redirecting.`)) return;
    await api.delete(`/dbas/${dba.id}/aliases/${alias}`);
    fetchDBAs();
  };

  const intakeUrl = (slug) => `${window.location.origin}/intake/${slug}`;

  const copyUrl = (slug) => {
    navigator.clipboard.writeText(intakeUrl(slug));
  };

  const uniqueAliases = (aliases = [], currentSlug = '') => Array.from(new Set(aliases.filter(alias => alias && alias !== currentSlug)));

  return (
    <div className="-mx-3 space-y-6 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0">
      <HighVisibilityPageHeader
        eyebrow="Business units"
        title="Businesses"
        subtitle="Manage DBAs, service lines, intake links, branding, and customer-facing URLs."
        countLabel={`${dbas.length} business units`}
        icon={Building2}
        tone="emerald"
        testId="dbas-high-visibility-header"
        titleTestId="dbas-title"
        actions={<Button className="h-14 rounded-2xl bg-white px-5 font-black text-emerald-700 hover:bg-emerald-50" onClick={openAdd} data-testid="add-dba-button"><Plus className="w-5 h-5 mr-2" /> Add Business</Button>}
      />

      <div className="grid grid-cols-1 gap-3 px-3 sm:px-0 md:grid-cols-2" data-testid="dbas-list">
        {dbas.length === 0 && <HighVisibilityEmptyState icon={Building2} title="No businesses yet" subtitle="Add your first DBA to start routing leads and jobs." testId="dbas-empty-state" />}
        {dbas.map(dba => (
          <Card key={dba.id} className="rounded-[1.5rem] border border-emerald-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md sm:rounded-md sm:border-zinc-200" data-testid={`dba-card-${dba.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-sm" style={{ backgroundColor: dba.color || '#059669' }}><Building2 className="h-6 w-6" /></div>
                  <div>
                    <h3 className="text-xl font-black text-zinc-900 font-heading">{dba.name}</h3>
                    {dba.parent_business_slug && <p className="text-xs font-semibold text-zinc-500" data-testid={`dba-parent-${dba.id}`}>Parent: {dba.parent_business_slug}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" className="h-12 rounded-2xl border-emerald-100 bg-emerald-50 text-emerald-700 sm:h-8 sm:rounded-sm" onClick={() => openEdit(dba)} data-testid={`edit-dba-${dba.id}`} aria-label="Edit business">
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Badge variant="secondary" className="mb-3 bg-blue-100 text-xs font-black text-blue-800">{industryLabels[dba.industry] || dba.industry}</Badge>
              {dba.status && <Badge variant="secondary" className={`ml-1 text-xs font-black mb-3 ${dba.status === 'coming_soon' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`dba-status-${dba.id}`}>{dba.status.replace('_', ' ')}</Badge>}
              {dba.review_link && <p className="mb-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700" data-testid={`dba-review-link-${dba.id}`}>Review link configured</p>}
              {uniqueAliases(dba.slug_aliases, dba.slug).length > 0 && (
                <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid={`dba-aliases-${dba.id}`}>
                  <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-amber-800">
                    <AlertTriangle className="h-3 w-3" /> Preserved old URL aliases
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {uniqueAliases(dba.slug_aliases, dba.slug).map((alias, index) => (
                      <span key={`${dba.id}-${alias}-${index}`} className="inline-flex min-h-9 items-center gap-1 rounded-2xl border border-amber-200 bg-white px-2 py-1 text-[11px] font-bold text-amber-800" data-testid={`dba-alias-${dba.id}-${index}`}>
                        /intake/{alias}
                        <button type="button" className="text-amber-700 hover:text-red-700" onClick={() => removeAlias(dba, alias)} data-testid={`remove-dba-alias-${dba.id}-${index}`} aria-label={`Remove alias ${alias}`}>
                          <Link2Off className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {dba.service_categories?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {dba.service_categories.map(cat => (
                    <span key={cat} className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-black text-emerald-700">{cat}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-col gap-2 border-t border-emerald-50 pt-3 sm:flex-row sm:items-center">
                <span className="flex-1 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-bold text-blue-800 sm:truncate" data-testid={`dba-intake-paths-${dba.id}`}>
                  Intake: /intake/{dba.slug} | Book: /book/{dba.slug}
                </span>
                <div className="grid grid-cols-2 gap-2 sm:flex">
                <Button variant="outline" className="h-12 rounded-2xl border-blue-100 bg-blue-50 text-blue-700 sm:h-8 sm:rounded-sm" onClick={() => copyUrl(dba.slug)} data-testid={`copy-intake-url-${dba.id}`} aria-label="Copy intake URL">
                  <Copy className="w-4 h-4" />
                </Button>
                <a href={intakeUrl(dba.slug)} target="_blank" rel="noopener noreferrer" className="inline-flex h-12 items-center justify-center rounded-2xl border border-indigo-100 bg-indigo-50 px-3 text-indigo-700 sm:h-8 sm:rounded-sm" data-testid={`open-intake-url-${dba.id}`} aria-label="Open intake URL">
                  <ExternalLink className="w-4 h-4" />
                </a>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-lg overflow-y-auto rounded-[1.5rem] bg-[#eef7ff] p-4 sm:bg-white sm:p-6">
          <DialogHeader className="rounded-[1.5rem] bg-gradient-to-br from-[#059669] via-[#0EA5E9] to-[#2563EB] p-4 text-white sm:bg-none sm:p-0 sm:text-zinc-950">
            <DialogTitle className="text-2xl font-black font-heading">{editing ? 'Edit Business' : 'New Business'}</DialogTitle>
            <DialogDescription className="font-semibold text-emerald-50 sm:text-zinc-500">{editing ? 'Update business details and services.' : 'Add a new DBA / business unit.'}</DialogDescription>
          </DialogHeader>
          {error && <p className="rounded-2xl bg-red-50 p-3 text-sm font-semibold text-red-600">{error}</p>}
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Business Name *</Label>
                <Input name="name" required defaultValue={editing?.name || ''} className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-name" />
              </div>
              <div>
                <Label>URL Slug *</Label>
                <Input name="slug" required value={slugDraft} onChange={(event) => setSlugDraft(event.target.value)} placeholder="e.g. junk-removal" className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-slug" />
              </div>
            </div>
            {slugChanged && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="dba-slug-change-warning">
                <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> URL slug is changing</p>
                <p className="mt-1 text-xs">The current slug <code className="rounded bg-white px-1">{editing.slug}</code> will be preserved as an alias so old intake, booking, and landing-page links keep redirecting.</p>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Status</Label>
                <select name="status" defaultValue={editing?.status || 'active'} className="h-12 w-full rounded-2xl border border-emerald-100 bg-emerald-50 px-3 text-base font-black text-emerald-800 sm:h-9 sm:rounded-md sm:bg-white sm:text-sm sm:font-normal" data-testid="dba-form-status">
                  <option value="active">Active</option>
                  <option value="coming_soon">Coming Soon</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <Label>Service Line</Label>
                <select name="service_line" defaultValue={editing?.service_line || editing?.industry || ''} className="h-12 w-full rounded-2xl border border-blue-100 bg-blue-50 px-3 text-base font-black text-blue-800 sm:h-9 sm:rounded-md sm:bg-white sm:text-sm sm:font-normal" data-testid="dba-form-service-line">
                  <option value="">Select...</option>
                  <option value="junk-removal">junk-removal</option>
                  <option value="pressure-washing">pressure-washing</option>
                  <option value="property-cleanup">property-cleanup</option>
                  <option value="deep-cleaning">deep-cleaning</option>
                  <option value="other">other</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Parent Business Slug</Label>
              <Input name="parent_business_slug" defaultValue={editing?.parent_business_slug || 'gmv-property-services'} placeholder="gmv-property-services" className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-parent-business-slug" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Industry *</Label>
                <select name="industry" required defaultValue={editing?.industry || ''} className="h-12 w-full rounded-2xl border border-violet-100 bg-violet-50 px-3 text-base font-black text-violet-800 sm:h-9 sm:rounded-md sm:bg-white sm:text-sm sm:font-normal" data-testid="dba-form-industry">
                  <option value="">Select...</option>
                  {Object.entries(industryLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Brand Color</Label>
                <Input name="color" type="color" defaultValue={editing?.color || '#059669'} className="h-12 rounded-2xl p-1 sm:h-9 sm:rounded-md" data-testid="dba-form-color" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editing?.description || ''} className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-description" />
            </div>
            <div>
              <Label>Service Categories</Label>
              <div className="space-y-2">
                {categories.map((cat, i) => (
                  <div key={`${cat || 'category'}-${i}`} className="flex gap-2">
                    <Input
                      value={cat}
                      onChange={(e) => {
                        const updated = [...categories];
                        updated[i] = e.target.value;
                        setCategories(updated);
                      }}
                      placeholder="e.g. Residential Cleaning"
                      className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm"
                      data-testid={`dba-form-category-${i}`}
                    />
                    {categories.length > 1 && (
                      <Button type="button" variant="outline" className="h-12 rounded-2xl border-red-100 bg-red-50 text-red-600 sm:h-9 sm:rounded-sm" onClick={() => setCategories(categories.filter((_, j) => j !== i))} data-testid={`remove-category-button-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" className="h-12 rounded-2xl border-emerald-100 bg-emerald-50 font-black text-emerald-700 sm:h-9 sm:rounded-sm" onClick={() => setCategories([...categories, ''])} data-testid="add-category-button">
                  <Plus className="w-4 h-4 mr-1" /> Add Category
                </Button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Phone</Label>
                <Input name="contact_phone" defaultValue={editing?.contact_phone || ''} className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input name="contact_email" type="email" defaultValue={editing?.contact_email || ''} className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-email" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input name="contact_address" defaultValue={editing?.contact_address || ''} className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-address" />
            </div>
            <div>
              <Label>Service Area</Label>
              <Input name="service_area" defaultValue={editing?.service_area || ''} placeholder="Butler, Hamilton, Warren, Preble counties" className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-service-area" />
            </div>
            <div className="mt-2 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 sm:border-t sm:border-zinc-200 sm:bg-transparent sm:p-0 sm:pt-4">
              <p className="mb-3 text-xs font-black uppercase tracking-wider text-emerald-700">Branding</p>
              <div className="space-y-3">
                <div>
                  <Label>Tagline</Label>
                  <Input name="tagline" defaultValue={editing?.tagline || ''} placeholder="e.g., Professional service you can trust" className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-tagline" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Business Hours</Label>
                    <Input name="business_hours" defaultValue={editing?.business_hours || ''} placeholder="e.g., Mon-Fri 8AM-5PM" className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-hours" />
                  </div>
                  <div>
                    <Label>Website URL</Label>
                    <Input name="website_url" defaultValue={editing?.website_url || ''} placeholder="https://..." className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-website" />
                  </div>
                </div>
                <div>
                  <Label>Review Link</Label>
                  <Input name="review_link" defaultValue={editing?.review_link || ''} placeholder="https://g.page/r/.../review" className="h-12 rounded-2xl border-emerald-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" data-testid="dba-form-review-link" />
                </div>
              </div>
            </div>
            {editing && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-3" data-testid="dba-delete-zone">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><Trash2 className="h-4 w-4" /> Delete business</p>
                    <p className="mt-1 text-xs text-red-700">Deletes only unreferenced business records. If leads, jobs, quotes, invoices, customers, or other records use this DBA, deletion is blocked.</p>
                  </div>
                  <Button type="button" variant="outline" className="h-12 rounded-2xl border-red-200 text-red-700 hover:bg-red-100 sm:h-9 sm:rounded-sm" onClick={() => openDelete(editing)} data-testid="open-dba-delete-dialog">
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" onClick={() => setShowForm(false)} data-testid="dba-form-cancel">Cancel</Button>
              <Button type="submit" className="h-12 rounded-2xl bg-emerald-600 font-black text-white hover:bg-emerald-700 sm:h-9 sm:rounded-sm" data-testid="dba-form-submit">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirm(''); setDeleteError(''); } }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-md rounded-[1.5rem] bg-[#fff7ed] p-4 sm:bg-white sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-800">Delete Business</DialogTitle>
            <DialogDescription>This action permanently removes the DBA record only when no operational records reference it.</DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="dba-delete-warning">
                <p className="font-semibold">{deleteTarget.name}</p>
                <p className="mt-1 text-xs">Deletion is blocked if this business has leads, jobs, quotes, invoices, customers, templates, messages, mileage, or other related records.</p>
              </div>
              {deleteError && <p className="rounded-2xl border border-red-200 bg-white p-2 text-xs text-red-700" data-testid="dba-delete-error">{deleteError}</p>}
              <div>
                <Label>Type business name or DELETE_BUSINESS</Label>
                <Input value={deleteConfirm} onChange={(event) => setDeleteConfirm(event.target.value)} placeholder={deleteTarget.name} className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" data-testid="dba-delete-confirm-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" onClick={() => { setDeleteTarget(null); setDeleteConfirm(''); setDeleteError(''); }} data-testid="dba-delete-cancel">Cancel</Button>
                <Button type="button" className="h-12 rounded-2xl bg-red-600 font-black hover:bg-red-700 sm:h-9 sm:rounded-sm" onClick={deleteDBA} disabled={!deleteConfirm || (deleteConfirm !== 'DELETE_BUSINESS' && deleteConfirm !== deleteTarget.name)} data-testid="dba-delete-confirm-button">
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete Business
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
